import * as z from 'zod';

import { Address } from '../../Address';
import { MiCompanyCode } from '../../investorReporting/MI/miCompanyCodes';
import { PayeeTaxOption } from '../../payee/Payee';
import { DueMonthDay } from '../../utils/Zod';

// Five ways "6/1" gets mangled before reaching this schema:
//   1. Excel re-saves the CSV with a year, producing "6/1/2025".
//   2. The file-import pipeline reads the CSV via xlsx with cellDates: true, which parses
//      date-shaped strings into JS Dates and then ISO-stringifies them to "2026-06-01".
//   3. The cell is text-formatted as "MMDD" (e.g. "0111"), with no separator.
//   4. Excel strips the leading zero from a numeric-looking MMDD cell, leaving 3 digits
//      ("0131" -> "131").
//   5. Vendor LATF files pad blank date cells to a fixed column width with spaces, so
//      "absent" arrives as "          " rather than "".
// Reshape any of these back to "M/d" so DueMonthDay's refine accepts it. The year is
// discarded — DueMonthDay is semantically month/day only.
export const preprocessDueMonthDay = (val: unknown): unknown => {
  if (typeof val !== 'string') return val;
  const trimmed = val.trim();
  if (trimmed === '') return '';
  const slashMatch = trimmed.match(/^(\d{1,2}\/\d{1,2})\/\d{2,4}$/);
  if (slashMatch) return slashMatch[1];
  const isoMatch = trimmed.match(/^\d{4}-(\d{1,2})-(\d{1,2})$/);
  if (isoMatch) return `${parseInt(isoMatch[1], 10)}/${parseInt(isoMatch[2], 10)}`;
  const mmddMatch = trimmed.match(/^(\d{2})(\d{2})$/);
  if (mmddMatch) return `${parseInt(mmddMatch[1], 10)}/${parseInt(mmddMatch[2], 10)}`;
  const mddMatch = trimmed.match(/^(\d)(\d{2})$/);
  if (mddMatch) return `${parseInt(mddMatch[1], 10)}/${parseInt(mddMatch[2], 10)}`;
  return trimmed;
};

export const optionalDueMonthDay = z.preprocess(
  preprocessDueMonthDay,
  z.union([z.literal(''), DueMonthDay]).optional(),
);

// LATF / xlsx exports pad string cells to fixed column widths with spaces. Trim before any
// other validation so "WI                  " is accepted as a state abbreviation, etc.
export const preprocessTrim = (val: unknown): unknown => (typeof val === 'string' ? val.trim() : val);

// Excel strips leading zeros from numeric-looking ZIPs ("06183" -> "6183"). Zero-pad short
// numeric values back to 5 digits so US-strict address validation still succeeds.
export const preprocessPostcode = (val: unknown): unknown => {
  if (typeof val !== 'string') return val;
  const trimmed = val.trim();
  if (/^\d{3,4}$/.test(trimmed)) return trimmed.padStart(5, '0');
  return trimmed;
};

export const optionalString = z.preprocess(preprocessTrim, z.string().optional());
export const optionalPostcode = z.preprocess(preprocessPostcode, z.string().optional());

// Tax option arrives as a number from flatfile or as a string from CSV; allow either and treat
// blank cells as absent so the default can be applied downstream.
export const preprocessTaxOption = (val: unknown): unknown => {
  if (val === undefined || val === null) return undefined;
  if (typeof val === 'string') {
    const trimmed = val.trim();
    if (trimmed === '') return undefined;
    const parsed = Number(trimmed);
    return Number.isNaN(parsed) ? trimmed : parsed;
  }
  return val;
};
export const optionalTaxOption = z.preprocess(preprocessTaxOption, PayeeTaxOption.optional());

// MI company codes are 3-char strings ("001", "038"). Excel strips leading zeros from
// numeric-looking cells, so "038" arrives as 38 or "38"; zero-pad back to 3 digits. Blank
// cells are treated as absent.
export const preprocessMiCompanyCode = (val: unknown): unknown => {
  if (val === undefined || val === null) return undefined;
  const str = typeof val === 'number' ? String(val) : typeof val === 'string' ? val.trim() : val;
  if (typeof str !== 'string' || str === '') return undefined;
  return /^\d{1,3}$/.test(str) ? str.padStart(3, '0') : str;
};
export const optionalMiCompanyCode = z.preprocess(preprocessMiCompanyCode, MiCompanyCode.optional());

export interface PayeeRowAddressFields {
  addressLine1?: string;
  addressLine2?: string;
  addressLocality?: string;
  addressRegion?: string;
  addressPostcode?: string;
  addressCountry?: string;
}

export const rowToAddress = (row: PayeeRowAddressFields): z.infer<typeof Address> => ({
  line1: row.addressLine1 ?? '',
  line2: row.addressLine2 || undefined,
  locality: row.addressLocality ?? '',
  region: row.addressRegion ?? '',
  postcode: row.addressPostcode ?? '',
  country: row.addressCountry?.trim() || 'USA',
});

export const isAddressBlank = (row: PayeeRowAddressFields): boolean =>
  !(row.addressLine1 ?? '').trim() &&
  !(row.addressLocality ?? '').trim() &&
  !(row.addressRegion ?? '').trim() &&
  !(row.addressPostcode ?? '').trim() &&
  !(row.addressCountry ?? '').trim();

export const ADDRESS_FIELD_KEYS = [
  'addressLine1',
  'addressLocality',
  'addressRegion',
  'addressPostcode',
  'addressCountry',
] as const;

export const FLAT_FIELD_BY_ADDRESS_KEY: Record<string, keyof PayeeRowAddressFields> = {
  line1: 'addressLine1',
  line2: 'addressLine2',
  locality: 'addressLocality',
  region: 'addressRegion',
  postcode: 'addressPostcode',
  country: 'addressCountry',
};
